import React, { useEffect, useRef } from 'react'
import {
  Widget,
  renderCustomComponent,
  toggleWidget,
} from '@ryaneewx/react-chat-widget'
import '@ryaneewx/react-chat-widget/lib/styles.css'
import './ChatWidget.scss'
import { marked } from 'marked'
import { useSelector } from 'react-redux'
import { getCurrentUser } from 'ducks/users/index.ts'
import logoSmall from '../logo_small.svg'
import addButtonIcon from '../../Editor/LeftNav/add-button.svg'
import { adaloBackendAxios } from '../../../utils/io/http/axios'

const ChatWidget = () => {
  const gitBookAIQuestion = async question => {
    try {
      const response = await adaloBackendAxios.post('/apps-chatbot-message', {
        question,
      })

      if (!response || response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return {
        success: true,
        data: {
          answer: response.data?.answer || '',
          sources: response.data?.sources || [],
        },
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      }
    }
  }

  const handleNewUserMessage = newMessage => {
    createTypingIndicator()
    gitBookAIQuestion(newMessage)
      .then(result => {
        if (!result.success) {
          hideTypingIndicator()
          renderCustomComponent('div', {
            dangerouslySetInnerHTML: {
              __html: composeChatMessage(
                'There was an error processing your request. Please try again later.'
              ),
            },
          })

          return
        }

        hideTypingIndicator()
        renderCustomComponent('div', {
          dangerouslySetInnerHTML: {
            __html: composeChatMessage(result.data.answer, true),
          },
        })
      })
      .catch(err => {
        hideTypingIndicator()
        renderCustomComponent('div', {
          dangerouslySetInnerHTML: {
            __html: composeChatMessage(
              'There was an error processing your request. Please try again later.'
            ),
          },
        })
      })
  }

  const composeChatMessage = (markdown, showHelpArticlesLink = false) => {
    const now = new Date()
    const [hours, minutes] = [
      now.getHours().toString().padStart(2, '0'),
      now.getMinutes().toString().padStart(2, '0'),
    ]

    const time = `${hours}:${minutes}`

    return `<div class="rcw-message custom-rcw-message">
              <img src="${logoSmall}" class="rcw-avatar " alt="profile">
              <div class="rcw-response">
                <div class="rcw-message-text custom-rcw-message-text">
                  ${marked.parse(markdown)}
                  ${
                    showHelpArticlesLink
                      ? `<br><br>
                    <p>You can always refer to <a href="https://help.adalo.com/" target="_blank">Adalo Help Center</a> for more information.</p>`
                      : ''
                  }
                  </div>
                <span class="rcw-timestamp">${time}</span>
              </div>
            </div>`
  }

  const createTypingIndicator = () => {
    if (document.querySelector('.rcw-typing-indicator-container')) {
      showTypingIndicator()

      return
    }

    const typingIndicator = document.createElement('div')
    typingIndicator.className = 'rcw-typing-indicator-container'
    typingIndicator.innerHTML = `<span>Adalo AI Chatbot is typing...</span>`
    document.querySelector('#rcw-messages')?.appendChild(typingIndicator)

    showTypingIndicator()
  }

  const hideTypingIndicator = () => {
    if (document.querySelector('.rcw-typing-indicator-container')) {
      document.querySelector('.rcw-typing-indicator-container').style.display =
        'none'
    }
  }

  const showTypingIndicator = () => {
    if (document.querySelector('.rcw-typing-indicator-container')) {
      document.querySelector('.rcw-typing-indicator-container').style.display =
        'block'
    }
  }

  const user = useSelector(state => getCurrentUser(state))
  const hasRendered = useRef(false)

  const initAIChatBotToggleFromIframe = () => {
    const observerIFrame = new MutationObserver((mutations, obsIframe) => {
      const iframe = document.querySelector(
        '.userflowjs-resource-center-frame--open'
      )

      if (iframe) {
        const aiChatBotButton =
          Array.from(
            iframe?.contentWindow?.document?.querySelectorAll?.(
              '.userflowjs-resource-center-block'
            ) ?? []
          ).find(btn =>
            Array.from(btn?.querySelectorAll?.('div') ?? []).some(div => {
              const text = (div.textContent ?? '').trim().toLowerCase()

              return (
                text.includes('ai chat bot') ||
                text.includes('ai chatbot') ||
                text.includes('ai chat')
              )
            })
          ) || null

        if (aiChatBotButton) {
          aiChatBotButton.onclick = e => {
            e.preventDefault()
            toggleWidget()
          }
        }
      }
    })

    observerIFrame.observe(
      document.querySelector('.userflowjs-resource-center-frame')?.contentWindow
        ?.document,
      {
        childList: true,
        subtree: true,
      }
    )
  }

  const waitForIframeToLoadAndStartObserving = () => {
    const timeoutId = setTimeout(() => {}, 500)

    const observer = new MutationObserver((mutations, obs) => {
      const target = document.querySelector('.userflowjs-resource-center-frame')

      if (target) {
        clearTimeout(timeoutId)
        initAIChatBotToggleFromIframe()
        obs.disconnect()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  const handleCopy = e => {
    const selection = window.getSelection()
    if (!selection) return

    const container = selection.anchorNode?.parentElement?.closest(
      '.rcw-message-text, .custom-rcw-message-text'
    )

    if (container) {
      e.clipboardData.setData('text/plain', selection.toString())
      e.preventDefault()
    }
  }

  useEffect(() => {
    if (!user) return
    renderCustomComponent('div', {
      dangerouslySetInnerHTML: {
        __html: composeChatMessage('Hi there! How can I help you out?'),
      },
    })

    waitForIframeToLoadAndStartObserving()

    // When copying text from the chat widget, copy the text content without the styling
    document.addEventListener('copy', handleCopy)

    hasRendered.current = true
  }, [user])

  return (
    <Widget
      launcher={() => null}
      handleNewUserMessage={handleNewUserMessage}
      title="Adalo AI Chatbot"
      profileAvatar={logoSmall}
      titleAvatar={logoSmall}
      launcherOpenImg={logoSmall}
      launcherCloseImg={addButtonIcon}
      senderPlaceHolder="Write your message..."
    />
  )
}

export default ChatWidget
